import React, { useEffect, useState } from "react";
import { api, fmt } from "@/api";
import { JerseyBadge } from "@/components/Shared";
import { Share2, Lock } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1553787762-b5f5721f3270?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxoYXdhaWklMjBiZWFjaCUyMHNob3JlfGVufDB8fHx8MTc4MzAyMDE3M3ww&ixlib=rb-4.1.0&q=85";
const MEDAL = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function Awards() {
  const [data, setData] = useState({ divisions: [], revealed: false, privileged: false });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => {
    api.awards().then((d) => { setData(d); setLoading(false); });
  }, []);

  const flash = (t) => { setToast(t); setTimeout(() => setToast(""), 2200); };

  const share = async (divId, name) => {
    const url = api.shareDivisionUrl(divId);
    if (navigator.share) {
      try { await navigator.share({ title: `${name} — Pounders Fest`, text: `Final standings for ${name}`, url }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); flash("Link copied — paste it anywhere to share"); }
    catch (e) { window.prompt("Copy this link to share:", url); }
  };

  if (loading) return <div className="p-8 text-center text-white/60 font-display uppercase tracking-widest">Loading…</div>;

  const { divisions, revealed, privileged } = data;

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A" }} data-testid="awards-view">
      <div
        className="relative py-12 px-4 text-center bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative">
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tighter text-white leading-none">
            Pounders Fest
          </h1>
          <p className="font-display uppercase tracking-[0.3em] text-[#FFD700] mt-2">Awards &amp; Final Results</p>
        </div>
      </div>

      {!revealed && (
        <div className="max-w-3xl mx-auto px-4 pt-6">
          <div className="border-4 border-[#FFD700] bg-[#FFD700]/10 p-4 flex items-center gap-3" data-testid="awards-locked-banner">
            <Lock size={22} className="text-[#FFD700] shrink-0" />
            <p className="font-display uppercase text-sm tracking-widest text-white">
              {privileged
                ? "Finals are hidden from the public — you're seeing an organizer preview. Flip “Reveal Results” in Setup at the ceremony."
                : "Champions are revealed live at the closing ceremony. Come find out who takes the podium!"}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto p-4 pb-24 space-y-8">
        {divisions.map((d) => {
          const locked = !d.results;
          return (
            <div key={d.division_id} data-testid={`awards-division-${d.division_id}`}>
              <div className="flex items-center justify-between gap-2 border-b-4 border-[#FFD700] pb-2 mb-4 flex-wrap">
                <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-white">
                  {d.division_name}
                </h2>
                {revealed && d.results && (
                  <button
                    data-testid={`awards-share-${d.division_id}`}
                    onClick={() => share(d.division_id, d.division_name)}
                    className="flex items-center gap-1.5 bg-[#FFD700] text-black font-display font-black uppercase text-xs tracking-widest px-3 py-2 active:scale-95 transition-transform"
                  >
                    <Share2 size={15} /> Share
                  </button>
                )}
              </div>
              {locked ? (
                <p className="font-display uppercase text-sm tracking-widest text-white/40 mb-2 flex items-center gap-2" data-testid={`awards-locked-${d.division_id}`}>
                  <Lock size={15} />
                  {!revealed
                    ? (d.has_final ? "Results locked — revealed at the ceremony" : "Final not scored yet")
                    : "Mark a heat as “Final” in Setup to show results here"}
                </p>
              ) : (
                <div className="space-y-2">
                  {d.results.map((r) => {
                    const podium = r.rank <= 3;
                    return (
                      <div
                        key={r.rider_id}
                        className="flex items-center gap-3 p-3 border-4"
                        style={{
                          borderColor: podium ? MEDAL[r.rank - 1] : "#333",
                          background: podium ? "rgba(255,255,255,0.06)" : "transparent",
                        }}
                        data-testid={`awards-place-${d.division_id}-${r.rank}`}
                      >
                        <div
                          className="font-score text-5xl w-10 text-center"
                          style={{ color: podium ? MEDAL[r.rank - 1] : "#666" }}
                        >
                          {r.rank}
                        </div>
                        <JerseyBadge jersey={r.jersey} />
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-black uppercase text-white truncate text-lg">{r.name}</div>
                          {podium && (
                            <div className="font-display text-xs uppercase tracking-widest" style={{ color: MEDAL[r.rank - 1] }}>
                              {["1st — Board", "2nd — Fins + Package", "3rd — Finalist"][r.rank - 1]}
                            </div>
                          )}
                        </div>
                        <div className="font-score text-5xl text-white">{fmt(r.total)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-white text-black font-display font-black uppercase tracking-widest px-6 py-3 z-50 text-center" data-testid="awards-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
